/**
 * SelectionButton Component
 * 
 * Card-style button for single/multi-select questions.
 * Features:
 * - Large touch target (mobile-friendly)
 * - Icon support with emoji fallback
 * - Description text
 * - Selected state with animation
 * - Keyboard accessible
 */

import React from 'react';
import { motion } from 'framer-motion';
import { QuestionOption } from '../../types/funnel.types';

// ============================================
// TYPES
// ============================================

interface SelectionButtonProps {
  /** Option data to display */
  option: QuestionOption;
  /** Whether this option is currently selected */
  isSelected: boolean;
  /** Click handler */
  onClick: () => void;
  /** Disable the button */
  disabled?: boolean;
  /** Index for staggered animation */
  index?: number;
}

// ============================================
// ANIMATION VARIANTS
// ============================================

/**
 * Animation variants for staggered appearance
 */
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: (index: number) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      delay: index * 0.05,
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94],
    },
  }),
};

// ============================================
// COMPONENT
// ============================================

/**
 * SelectionButton - Large, touch-friendly option button
 */
export const SelectionButton: React.FC<SelectionButtonProps> = ({
  option,
  isSelected,
  onClick,
  disabled = false,
  index = 0,
}) => {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled}
      custom={index}
      variants={itemVariants}
      initial="hidden"
      animate="visible"
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      className={`
        w-full min-h-selection p-4 sm:p-5
        rounded-xl border-2
        text-left
        transition-all duration-200
        focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2
        disabled:opacity-50 disabled:cursor-not-allowed
        ${isSelected 
          ? 'border-primary bg-primary/5 shadow-md' 
          : 'border-border bg-white hover:border-primary/50 hover:shadow-card-hover'
        }
      `}
      aria-pressed={isSelected}
    >
      <div className="flex items-start gap-4">
        {/* Icon/Emoji */}
        {option.icon && (
          <motion.span
            initial={{ scale: 1 }}
            animate={{ scale: isSelected ? 1.1 : 1 }}
            className="text-2xl sm:text-3xl flex-shrink-0 mt-0.5"
            aria-hidden="true"
          >
            {option.icon}
          </motion.span>
        )}

        {/* Text content */}
        <div className="flex-grow min-w-0">
          {/* Main label */}
          <span
            className={`
              block text-base sm:text-lg font-medium
              transition-colors duration-200
              ${isSelected ? 'text-primary' : 'text-text-primary'}
            `}
          >
            {option.label}
          </span>

          {/* Description (if provided) */}
          {option.description && (
            <span
              className={`
                block mt-1 text-sm
                transition-colors duration-200
                ${isSelected ? 'text-primary/70' : 'text-text-secondary'}
              `}
            >
              {option.description}
            </span>
          )}
        </div>

        {/* Selection indicator (checkbox/radio style) */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ 
            scale: isSelected ? 1 : 0.8, 
            opacity: isSelected ? 1 : 0.3 
          }}
          className={`
            w-6 h-6 rounded-full flex-shrink-0
            flex items-center justify-center
            transition-colors duration-200
            ${isSelected 
              ? 'bg-primary text-white' 
              : 'bg-border text-transparent'
            }
          `}
          aria-hidden="true"
        >
          {/* Checkmark icon */}
          <svg
            className="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </motion.div>
      </div>
    </motion.button>
  );
};

SelectionButton.displayName = 'SelectionButton';

// ============================================
// SELECTION GROUP COMPONENT
// ============================================

interface SelectionGroupProps {
  /** Array of options to display */
  options: QuestionOption[];
  /** Currently selected value(s) */
  value: string | string[] | undefined;
  /** Change handler */
  onChange: (value: string) => void;
  /** Allow multiple selections */
  multiple?: boolean;
  /** Disable all options */
  disabled?: boolean;
  /** Grid columns (1-3) */
  columns?: 1 | 2 | 3;
}

/**
 * SelectionGroup - Container for multiple SelectionButtons
 * Handles single and multi-select logic
 */
export const SelectionGroup: React.FC<SelectionGroupProps> = ({
  options,
  value,
  onChange,
  multiple = false,
  disabled = false,
  columns = 1,
}) => {
  // Check if an option is selected
  const isSelected = (optionId: string): boolean => {
    if (!value) return false;
    if (Array.isArray(value)) return value.includes(optionId);
    return value === optionId;
  };

  // Grid classes based on column count
  const gridClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  };

  return (
    <div
      className={`grid ${gridClasses[columns]} gap-3`}
      role={multiple ? 'group' : 'radiogroup'}
      aria-label="Select an option"
    >
      {options.map((option, index) => (
        <SelectionButton
          key={option.id}
          option={option}
          isSelected={isSelected(option.id)}
          onClick={() => onChange(option.id)}
          disabled={disabled}
          index={index}
        />
      ))}
    </div>
  );
};

SelectionGroup.displayName = 'SelectionGroup';

export default SelectionButton;
